import { useCallback, useEffect, useRef } from 'preact/hooks'

/**
 * Coalesces a burst of field edits into a single save.
 *
 * Panels that persist as you type call `patch` on every keystroke. Without this, each
 * keystroke is its own request — and because the entity behind them is written under an
 * optimistic-concurrency ETag, only the first request in a burst can win. Every one after it
 * is judged against an ETag its predecessor has already superseded, so it comes back a
 * conflict. Typing a CTA label into the landing page builder produced one save per character
 * and an error for all but the first (ORB-760).
 *
 * Edits accumulate into one pending patch rather than replacing each other, so a save carries
 * the whole burst — including edits to different fields — not just its last keystroke.
 *
 * `flush` sends whatever is pending immediately, for a caller that cannot wait out the timer.
 * Unmounting flushes too: a panel closed mid-word should keep the word, not drop it.
 */
export function useDebouncedPatch<T extends object>(
    save: (patch: Partial<T>) => void | Promise<unknown>,
    delay = 800,
) {
    const pendingRef = useRef<Partial<T> | null>(null)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // The save closure changes identity on most renders (it depends on the loaded entity), and
    // a scheduled timer must fire the newest one, not whichever existed when it was set.
    const saveRef = useRef(save)
    useEffect(() => { saveRef.current = save }, [save])

    // A save that has left but not landed still counts as unsettled work: its response is the
    // caller's own payload coming back, and adopting that over what has been typed since would
    // revert it. See `isSettling`.
    const savesInFlight = useRef(0)

    const flush = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
        const pending = pendingRef.current
        pendingRef.current = null
        if (!pending) return

        savesInFlight.current += 1
        const settled = () => { savesInFlight.current -= 1 }

        // A save that fails still has to give the count back, or the panel never adopts a server
        // value again. How the failure itself surfaces is deliberately left alone: a synchronous
        // throw propagates out of flush as it always did, and a rejected promise stays unhandled,
        // so neither is quietly swallowed here.
        let result: void | Promise<unknown>
        try {
            result = saveRef.current(pending)
        } catch (error) {
            settled()
            throw error
        }
        void Promise.resolve(result).finally(settled)
    }, [])

    const patch = useCallback((fields: Partial<T>) => {
        pendingRef.current = { ...(pendingRef.current ?? {}), ...fields }
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(flush, delay)
    }, [delay, flush])

    /**
     * Whether an edit is still on its way to the server — waiting on the timer, or sent and not
     * yet answered. Callers read it to decide whether a server value may be adopted over what the
     * user is typing.
     *
     * Read at the moment it matters rather than tracked by the caller: a counter kept in step by
     * hand drifts the first time an edit takes a path that does not save, and the caller cannot
     * see the in-flight half at all, because the debounce is what issues the save.
     */
    const isSettling = useCallback(() => pendingRef.current !== null || savesInFlight.current > 0, [])

    // Held in a ref so the unmount cleanup below can stay dependency-free — depending on
    // `flush` would re-run it on every identity change and save mid-edit.
    const flushRef = useRef(flush)
    useEffect(() => { flushRef.current = flush }, [flush])

    useEffect(() => () => { flushRef.current() }, [])

    return { patch, flush, isSettling }
}
